import React, {
  useContext,
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect,
} from "react";
import { FixedSizeTree as Tree } from "react-vtree";
import { FixedSizeList as List } from "react-window";
import {
  Center,
  Loader,
  Stack,
  ThemeIcon,
  Text,
  Button,
  Checkbox,
  Grid,
  ActionIcon,
  Group,
  Divider,
} from "@mantine/core";
import {
  IconFolder,
  IconChevronDown,
  IconFolderPlus,
  IconClipboardList,
  IconFolderOpen,
} from "@tabler/icons-react";
import FolderMenu from "../components/FolderMenu";
import { FoldersContext } from "../contexts/FoldersContext";
import FolderTooltip from "./FolderTooltip";
import FolderSearchResultsRow from "./FolderSearchResultsRow";
import { useMounted } from "@mantine/hooks";

function FolderVTree({
  height,
  width,
  itemSize = 32,
  enableSelect,
  enableFilters = false,
  selected,
  onSelect,
  showFolderActionMenu = true,
  showFolderCreateButton = true,
  disabledFolderActionMenuItems = [],
  searchResults = [],
  searchQuery = "",
  onSearchClear,
  toggleFolderFilter: toggleFolderFilterProp,
  "data-testid": dataTestId,
}) {
  const {
    sortedFolders,
    loading,
    searching,
    folderFiltersLoading,
    setFolderToModify,
    openCreate,
    canCreateFolder,
    viewingFolder,
    setViewingFolder,
    toggleFolderFilter,
    folderFilters,
    expandedCheckedNodes,
    collapseAll,
  } = useContext(FoldersContext);
  const treeRef = useRef(null);
  const mounted = useMounted();
  const disabledRootFolderActionMenuItems = ["delete", "rename", "move"];

  const filterSet = useMemo(
    () => new Set(folderFilters?.folderFilters?.map((f) => String(f.id)) || []),
    [folderFilters]
  );

  const rootChildren = useMemo(
    () => sortedFolders[0]?.children || [],
    [sortedFolders]
  );

  const getPathsToCheckedNodes = useCallback(() => {
    if (!enableFilters || !filterSet || filterSet.size === 0) {
      return new Set();
    }

    const pathsToExpand = new Set();

    const traverse = (node, currentPath = []) => {
      const nodeId = String(node.value);
      const newPath = [...currentPath, nodeId];

      if (filterSet.has(nodeId)) {
        newPath.forEach((pathId) => pathsToExpand.add(pathId));
      }

      if (node.children) {
        node.children.forEach((child) => traverse(child, newPath));
      }
    };

    sortedFolders.forEach((root) => traverse(root));
    return pathsToExpand;
  }, [sortedFolders, enableFilters, filterSet]);

  const handleCreateFolder = useCallback(
    (e, folder) => {
      e.stopPropagation();
      setFolderToModify(folder);
      openCreate();
    },
    [setFolderToModify, openCreate]
  );

  const handleToggleFolderFilter = useCallback(
    async (folderId) => {
      if (enableFilters) {
        const folderFilterFunction =
          toggleFolderFilterProp || toggleFolderFilter;
        await folderFilterFunction(folderId);
      } else if (onSelect && enableSelect) {
        setViewingFolder(folderId);
        onSelect(folderId);
      }
    },
    [
      enableFilters,
      toggleFolderFilterProp,
      toggleFolderFilter,
      setViewingFolder,
      onSelect,
    ]
  );

  const descendantLookup = useMemo(() => {
    const lookup = {};
    function traverse(node) {
      let leafIds = [];
      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => {
          leafIds = leafIds.concat(traverse(child));
        });
      } else {
        leafIds = [String(node.value)];
      }
      lookup[String(node.value)] = {
        leafIds,
        totalLeafCount: leafIds.length,
      };
      return leafIds;
    }
    sortedFolders.forEach((root) => traverse(root));
    return lookup;
  }, [sortedFolders]);

  useEffect(() => {
    if (viewingFolder && mounted) {
      setTimeout(() => {
        setViewingFolder(null);
      }, 4000);
      if (treeRef.current) {
        treeRef.current.scrollToItem(String(viewingFolder.value), "center");
      }
    }
  }, [viewingFolder, mounted, treeRef]);

  const treeWalker = useCallback(
    function* treeWalker(refresh) {
      const stack = [];
      sortedFolders.forEach((root) =>
        stack.push({ node: root, nestingLevel: 0 })
      );

      const nodesToExpand = expandedCheckedNodes
        ? getPathsToCheckedNodes()
        : new Set();

      while (stack.length) {
        const { node, nestingLevel } = stack.pop();
        const children = node.children || [];
        const id = String(node.value);
        const isLeaf = children.length === 0;
        const isRoot = nestingLevel === 0;

        const { leafIds, totalLeafCount } = descendantLookup[id] || {
          leafIds: [id],
          totalLeafCount: 1,
        };
        const checkedCount = enableFilters
          ? leafIds.filter((lid) => filterSet?.has(lid)).length
          : 0;
        const checked = totalLeafCount > 0 && checkedCount === totalLeafCount;
        const indeterminate = checkedCount > 0 && checkedCount < totalLeafCount;

        let shouldExpand = false;
        if (collapseAll) {
          shouldExpand = isRoot;
        } else if (expandedCheckedNodes) {
          shouldExpand = isRoot || nodesToExpand.has(id);
        } else {
          shouldExpand = isRoot || viewingFolder?.path.includes(id);
        }

        const item = refresh
          ? {
              id,
              isLeaf,
              isRoot,
              nestingLevel,
              isOpenByDefault: shouldExpand,
              node,
              checked,
              indeterminate,
              viewing: viewingFolder?.value === id,
            }
          : id;
        const isOpened = yield item;

        if (children.length && isOpened) {
          for (let i = children.length - 1; i >= 0; i--) {
            stack.push({
              node: children[i],
              nestingLevel: nestingLevel + 1,
            });
          }
        }
      }
    },
    [
      sortedFolders,
      descendantLookup,
      enableFilters,
      filterSet,
      viewingFolder,
      expandedCheckedNodes,
      collapseAll,
      getPathsToCheckedNodes,
    ]
  );

  const Node = ({ data, isOpen, toggle, style }) => {
    const {
      node,
      nestingLevel,
      isLeaf,
      checked,
      indeterminate,
      viewing,
      isRoot,
    } = data;
    const [hovered, setHovered] = useState(false);

    return (
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          paddingLeft: nestingLevel * 20 + 14,
          paddingRight: 12,
          gap: 4,
        }}
      >
        {!enableSelect && (
          <Checkbox.Indicator
            checked={checked}
            indeterminate={indeterminate}
            onClick={() => handleToggleFolderFilter(node?.value)}
          />
        )}
        <FolderTooltip folder={node}>
          <div
            onClick={
              isLeaf
                ? () =>
                    enableSelect
                      ? onSelect(node)
                      : handleToggleFolderFilter(node?.value)
                : () => {
                    toggle();
                    if (enableSelect) onSelect(node);
                  }
            }
            className={`folder-node ${viewing ? "viewing" : ""}`}
            data-selected={selected?.value === node?.value}
            style={{
              flex: 1,
              flexWrap: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 0px 4px 2px",
            }}
          >
            {!isLeaf ? (
              <ActionIcon
                variant="subtle"
                size="sm"
                radius="sm"
                color="gray"
                ml={2}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle();
                }}
              >
                <IconChevronDown
                  size={16}
                  style={{
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </ActionIcon>
            ) : (
              <ThemeIcon variant="subtle" color="default" size="sm" radius="sm">
                <IconClipboardList size={16} />
              </ThemeIcon>
            )}
            {isOpen ? <IconFolderOpen size={16} /> : <IconFolder size={16} />}
            <Grid flex={1} align="center" style={{ flexWrap: "nowrap" }}>
              <Grid.Col span="auto">
                <Text
                  size="sm"
                  lineClamp={1}
                  style={{ wordBreak: "break-word", userSelect: "none" }}
                >
                  {node.label}
                </Text>
              </Grid.Col>
              {hovered && (
                <Grid.Col span="content">
                  <Group
                    gap={2}
                    wrap="nowrap"
                    onClick={(e) => e.stopPropagation()}
                    mr={4}
                  >
                    {showFolderCreateButton && canCreateFolder && (
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        onClick={(e) => handleCreateFolder(e, node)}
                      >
                        <IconFolderPlus size={16} />
                      </ActionIcon>
                    )}
                    {showFolderActionMenu && (
                      <FolderMenu
                        folder={node}
                        disabledItems={
                          isRoot
                            ? disabledRootFolderActionMenuItems
                            : disabledFolderActionMenuItems
                        }
                      />
                    )}
                  </Group>
                </Grid.Col>
              )}
            </Grid>
          </div>
        </FolderTooltip>
      </div>
    );
  };

  if (loading || folderFiltersLoading) {
    return (
      <Center style={{ height, width }}>
        <Loader size="sm" color="gray" />
      </Center>
    );
  }

  if (searching) {
    return (
      <Center style={{ height, width }}>
        <Stack align="center" gap="xs">
          <Loader size="sm" color="gray" />
          <Text size="sm" c="dimmed">
            Searching folders...
          </Text>
        </Stack>
      </Center>
    );
  }

  if (searchQuery !== "") {
    const SearchResultRow = ({ index, style }) => {
      const result = searchResults[index];
      return (
        <div style={{ ...style, paddingLeft: 12 }}>
          <FolderSearchResultsRow
            folder={result}
            enableView
            enableSelect={enableSelect}
            selected={selected}
            onSelect={onSelect}
            searchQuery={searchQuery}
            onSearchClear={onSearchClear}
            toggleFolderFilter={handleToggleFolderFilter}
            filterSet={filterSet}
            enableFilters={enableFilters}
          />
        </div>
      );
    };

    return searchResults.length === 0 ? (
      <Stack align="center" gap={3} my={30}>
        <ThemeIcon color="gray" size={50} radius={50} variant="light">
          <IconFolder size={20} />
        </ThemeIcon>
        <Text size="md" pt="md" ta="center">
          No folders found
        </Text>
        <Text c="dimmed" size="sm">
          Try adjusting your search query
        </Text>
      </Stack>
    ) : (
      <Stack gap={0}>
        <List
          height={height - 50}
          itemCount={searchResults.length}
          itemSize={36}
          width={width}
        >
          {SearchResultRow}
        </List>
        <Divider />
        <Group>
          <Text size="xs" c="dimmed" p={16}>
            {searchResults.length} results found
          </Text>
        </Group>
      </Stack>
    );
  }

  if (rootChildren.length === 0) {
    return (
      <Center style={{ height, width }}>
        <Stack align="center" gap="xs">
          <ThemeIcon color="gray" size={50} radius={50} variant="light">
            <IconFolder size={20} />
          </ThemeIcon>
          <Stack gap={0} align="center">
            <Text size="md">No folders found</Text>
            <Text c="dimmed" size="sm" ta="center">
              {canCreateFolder
                ? "Try creating a new folder"
                : "Ask an admin to create a folder"}
            </Text>
          </Stack>
          {canCreateFolder && (
            <Button
              mt="sm"
              leftSection={<IconFolderPlus size={16} />}
              variant="light"
              onClick={() => {
                setFolderToModify(sortedFolders[0]);
                openCreate();
              }}
            >
              Create folder
            </Button>
          )}
        </Stack>
      </Center>
    );
  }

  return (
    <div data-testid={dataTestId}>
      <Tree
        key={`tree-${expandedCheckedNodes}-${collapseAll}`}
        ref={treeRef}
        treeWalker={treeWalker}
        itemSize={itemSize}
        height={height}
        width={width}
      >
        {Node}
      </Tree>
    </div>
  );
}

export default React.memo(FolderVTree);
